import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useOrgScopedQuery } from "../../query/scope";
import { HTTP_NOT_FOUND, HttpError } from "./api";
import { ProvisioningGuideModal } from "./ProvisioningGuideModal";
import { TokenUpdateModal } from "./TokenUpdateModal";
import { canIssueRepoHostToken, issueRepoHostToken } from "./tokensApi";

export interface AddTokenFlowProps {
  readonly role: string;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

// Тело модалки авто-выпуска токена роли (POST /issue).
function IssueTokenBody(props: {
  readonly role: string;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { role, onClose, onSuccess } = props;
  const queryClient = useQueryClient();

  const [issueError, setIssueError] = useState<string | null>(null);

  const issueMutation = useMutation({
    mutationFn: () => issueRepoHostToken(role),
    onSuccess: () => {
      setIssueError(null);
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "tokens"],
      });
      onSuccess();
      onClose();
    },
    onError: (err) => {
      setIssueError(t("connections.genericError", { message: err.message }));
    },
  });

  return (
    <Stack gap="sm">
      <Text size="sm">
        {t("connections.tokens.addFlow.issueDescription", { role })}
      </Text>
      <Text size="sm" c="dimmed">
        {t("connections.tokens.addFlow.issueTemplate", { role })}
      </Text>

      {issueError !== null ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {issueError}
        </Alert>
      ) : null}

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("connections.modalCancel")}
        </Button>
        <Button
          onClick={() => {
            issueMutation.mutate();
          }}
          loading={issueMutation.isPending}
        >
          {t("connections.tokens.addFlow.issueButton")}
        </Button>
      </Group>
    </Stack>
  );
}

// Поток добавления токена для роли:
// - если can_issue=true → модалка авто-выпуска (POST /issue),
// - если can_issue=false → показ ProvisioningGuideModal (ручная инструкция),
// - пользователь также может переключиться на ручной ввод (TokenUpdateModal).
export function AddTokenFlow(props: AddTokenFlowProps): ReactElement {
  const { t } = useTranslation();
  const { role, onClose, onSuccess } = props;

  const [showManualForm, setShowManualForm] = useState(false);

  const canIssueQuery = useOrgScopedQuery({
    key: ["connections", "forgejo", "can-issue", role],
    queryFn: () => canIssueRepoHostToken(role),
    retry: false,
  });

  if (showManualForm) {
    return (
      <TokenUpdateModal
        opened
        onClose={onClose}
        role={role}
        onSuccess={onSuccess}
      />
    );
  }

  if (canIssueQuery.isPending) {
    return (
      <Modal
        opened
        onClose={onClose}
        title={t("connections.tokens.addFlow.title", { role })}
        size="sm"
      >
        <Loader />
      </Modal>
    );
  }

  if (canIssueQuery.isError || canIssueQuery.data === undefined) {
    // 404 от can-issue = подключение к Forgejo вообще не настроено (нет
    // org_repo_host_config / PAT). Это не «ошибка загрузки», а ожидаемый
    // краевой случай — подсказываем настроить подключение, а не предлагаем
    // ручной ввод токена (он без настроенного подключения тоже бессмыслен).
    const notConfigured =
      canIssueQuery.error instanceof HttpError &&
      canIssueQuery.error.status === HTTP_NOT_FOUND;

    return (
      <Modal
        opened
        onClose={onClose}
        title={t("connections.tokens.addFlow.title", { role })}
        size="sm"
      >
        {notConfigured ? (
          <Stack gap="sm">
            <Alert
              color="blue"
              title={t("connections.tokens.addFlow.notConfiguredTitle")}
            >
              {t("connections.tokens.addFlow.notConfiguredBody", { role })}
            </Alert>
            <Group justify="flex-end">
              <Button onClick={onClose}>
                {t("connections.tokens.addFlow.notConfiguredCta")}
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="sm">
            <Alert color="red">{t("connections.loadingError")}</Alert>
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                {t("connections.modalCancel")}
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  setShowManualForm(true);
                }}
              >
                {t("connections.tokens.addFlow.manualFallback")}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    );
  }

  const { can_issue } = canIssueQuery.data;

  if (can_issue) {
    return (
      <Modal
        opened
        onClose={onClose}
        title={t("connections.tokens.addFlow.title", { role })}
        size="sm"
      >
        <IssueTokenBody role={role} onClose={onClose} onSuccess={onSuccess} />
      </Modal>
    );
  }

  // can_issue=false → сразу показываем ProvisioningGuideModal.
  return <ProvisioningGuideModal opened onClose={onClose} role={role} />;
}
